<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { match } from "@/helpers/string";
import { CandidateOrigin } from '@/types/worker';

import type { supporttedEventNames } from "@/dsComponents/Dropdown/types";
import type { LabelValue } from '@/types/common';

import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import ControlledDropdown from "@/dsComponents/Dropdown/ControlledDropdown.vue";
import DropdownInput from "@/dsComponents/Dropdown/DropdownInput.vue";
import DropdownItem from "@/dsComponents/Dropdown/DropdownItem.vue";

interface CandidateOriginsDropdownProps {
    modelValue: LabelValue | null;
    placeholder?: string;
    icon?: string;
    error?: boolean;
    width?: string;
    height?: string;
    fitContent?: boolean;
    fullWidth?: boolean;
}

withDefaults(defineProps<CandidateOriginsDropdownProps>(), {
    icon: "door-enter",
    width: "320px",
    height: "220px",
    fullWidth: false,
});

const emit = defineEmits<{
    "update:modelValue": [value: LabelValue | null];
}>();

const { t } = useI18n();

const open = ref(false);
const originSearch = ref("");

const candidateOrigins = computed(() => {
    return Object.entries(CandidateOrigin)
        .filter(([, label]) => label !== "")
        .map(([value, label]) => ({ label, value }));
});

const filteredOrigins = computed(() => {
    if (!originSearch.value) return candidateOrigins.value;
    return candidateOrigins.value.filter((origin) => match(origin.label, originSearch.value));
});

const handleDropdownChange = (eventName: supporttedEventNames) => {
    if (eventName === "click") {
        open.value = !open.value;
    } else if (eventName === "clickaway") {
        open.value = false;
    }
};

const handleOriginSelect = (originValue: string) => {
    emit("update:modelValue", candidateOrigins.value.find((origin) => origin.value === originValue) ?? null);
    open.value = false;
};
</script>

<template>
    <ControlledDropdown
        :open="open"
        :fit-content="fitContent"
        :width="width"
        :height="height"
        :full-width="fullWidth"
        @change="handleDropdownChange"
    >
        <template #trigger-zone>
            <ComboButton :focus="open" :icon-left="icon" :placeholder="!modelValue" :full-width="fullWidth" :error="error">
                {{ modelValue?.label ?? placeholder ?? t("form.placeholders.origin") }}
            </ComboButton>
        </template>
        <template #dropdown-content>
            <DropdownInput v-model="originSearch" :focused="open" />
            <div class="origins-container">
                <DropdownItem
                    v-for="origin of filteredOrigins"
                    :key="origin.value"
                    :checked="modelValue?.value === origin.value"
                    @click="handleOriginSelect(origin.value)"
                >
                    {{ origin.label }}
                </DropdownItem>
            </div>
        </template>
    </ControlledDropdown>
</template>

<style scoped lang="scss">
.origins-container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs, 4px);
    padding-top: var(--spacing-xs, 4px);
    overflow-y: auto;
}
</style>
